import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  ViewChild,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { QuestionnaireBase } from '../../interfaces/questionnaire-base.interface';
import { QuestionnaireHostDirective } from '../../directives/questionnaire-host.directive';
import { SimpleQuestionnaireComponent } from '../simple-questionnaire/simple-questionnaire.component';
import {
  HabitsConfiguration,
  QuestionnaireCreate,
} from 'src/app/analyses/interfaces/analyses.types';
import {
  GpuFormFieldAppearance,
  GpuFormFieldModule,
} from '@geneplanet/ngx-ui/src/lib/form-field';
import {
  FormBuilder,
  FormGroupDirective,
  ReactiveFormsModule,
} from '@angular/forms';
import { PhysicalQuestionnaireComponent } from '../physical-questionnaire/physical-questionnaire.component';
import { AnswerDict } from '../../interfaces/answer-dict.interface';
import { FormPageFooterComponent } from 'src/app/shared/components/form-page-footer/form-page-footer.component';
import { TranslateModule } from '@ngx-translate/core';
import { QuestionnaireDateComponent } from '../questionnaire-date/questionnaire-date.component';
import { GpuLoadingModule } from '@geneplanet/ngx-ui/src/lib/loading';
import { AnalysesService } from 'src/app/analyses/services/analyses.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AnalyticsModule } from '@geneplanet/ngx-utils/src/lib/analytics';
// eslint-disable-next-line @typescript-eslint/naming-convention
import * as Sentry from '@sentry/angular';
import { ngbDateToNativeUTCDateTime } from 'src/app/shared/util/date-util';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';

enum ComponentState {
  LOADING = 'loading',
  ERROR = 'error',
  FORM = 'form',
}

@UntilDestroy()
@Component({
  selector: 'app-questionnaire-host',
  standalone: true,
  imports: [
    CommonModule,
    QuestionnaireHostDirective,
    ReactiveFormsModule,
    FormPageFooterComponent,
    TranslateModule,
    GpuFormFieldModule,
    QuestionnaireDateComponent,
    GpuLoadingModule,
    AnalyticsModule,
  ],
  templateUrl: './questionnaire-host.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuestionnaireHostComponent implements OnChanges {
  @ViewChild(QuestionnaireHostDirective, { static: true })
  questionnaireHost!: QuestionnaireHostDirective;

  @Input({ required: true }) questionnaires: HabitsConfiguration[];
  @Input() formFieldAppearance = GpuFormFieldAppearance.Card;
  @Input() smallTitle = false;
  @Output() completed = new EventEmitter<{ id: string }>();

  protected state = ComponentState.LOADING;
  protected GpuFormFieldAppearance = GpuFormFieldAppearance;

  protected questionnaireIndex = signal(0);
  protected form = this.fb.nonNullable.record({});

  protected get isSingleResult(): boolean {
    return this.questionnaires?.length === 1;
  }

  private answers = signal<AnswerDict>({});

  private componentRef: ComponentRef<QuestionnaireBase>;

  constructor(
    private fb: FormBuilder,
    private cdRef: ChangeDetectorRef,
    private analysesService: AnalysesService
  ) {}

  ngOnChanges() {
    if (this.questionnaires && !this.componentRef) {
      this.init();
    }
  }

  protected previous() {
    if (this.questionnaireIndex() > 0) {
      this.questionnaireIndex.update((current) => current - 1);
      this.loadComponent();
    }
  }

  protected submit(ngForm: FormGroupDirective) {
    if (this.form.invalid) {
      return;
    }

    const value = this.componentRef.instance.onSubmit();
    this.answers.update((current) => ({
      ...current,
      ...value,
    }));

    const date = this.form.controls.date?.value || undefined;

    // Destroying inner component, to prevent any events emitting when resetForm() is called.
    this.questionnaireHost.viewContainerRef.clear();

    // This is required to reset `submitted` on NgForm - otherwise errors can show
    // on untouched forms
    ngForm.resetForm();

    if (this.questionnaireIndex() + 1 < this.questionnaires.length) {
      this.questionnaireIndex.update((current) => current + 1);
      this.loadComponent();
    } else {
      this.submitAnswers(date as NgbDateStruct);
    }
  }

  private init() {
    if (!this.questionnaires?.length) {
      return;
    }

    this.loadComponent();
  }

  private loadComponent() {
    const componentSelector =
      this.questionnaires?.[this.questionnaireIndex()]?.id;
    if (!componentSelector) {
      return;
    }

    const component = this.selectComponent(componentSelector);
    this.state = ComponentState.FORM;

    const viewContainerRef = this.questionnaireHost.viewContainerRef;
    viewContainerRef.clear();

    this.componentRef =
      viewContainerRef.createComponent<QuestionnaireBase>(component);
    this.componentRef.instance.questionnaire =
      this.questionnaires[this.questionnaireIndex()];
    this.componentRef.instance.formFieldAppearance = this.formFieldAppearance;
    this.componentRef.instance.answers = this.questionnaireAnswers();
    this.componentRef.instance.smallTitle = this.smallTitle;

    // Init is optional
    if (this.componentRef.instance.init) {
      this.componentRef.instance.init();
    }

    this.cdRef.markForCheck();
  }

  private selectComponent(analysisId: string) {
    return (
      {
        ['85c0adce-e9ad-4e90-b327-7cb55410f975']:
          PhysicalQuestionnaireComponent,
      }[analysisId] ?? SimpleQuestionnaireComponent
    );
  }

  private questionnaireAnswers(): AnswerDict {
    const questionnaire = this.questionnaires[this.questionnaireIndex()];
    const ids = questionnaire.questions.map((q) => q.id);
    return Object.fromEntries(
      Object.entries(this.answers()).filter(([id]) => ids.includes(id))
    );
  }

  private submitAnswers(date: null | NgbDateStruct): void {
    const answerList = Object.entries(this.answers()).map(([key, value]) => {
      return {
        id: key,
        answers: value,
      };
    });

    const payload: QuestionnaireCreate = {
      questions: answerList,
    };

    if (this.isSingleResult) {
      payload.generated = ngbDateToNativeUTCDateTime(date).toISOString();
    }

    this.state = ComponentState.LOADING;
    this.questionnaireHost.viewContainerRef.clear();
    this.cdRef.markForCheck();

    this.analysesService
      .saveQuestionnaire(payload)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (response) => {
          this.completed.emit(response);
        },
        error: (error) => {
          this.state = ComponentState.ERROR;
          this.cdRef.markForCheck();

          Sentry.withScope((scope) => {
            scope.setExtra('payload', payload);
            scope.setExtra('error', error);
            Sentry.captureException('[QUESTIONNAIRE SUBMIT] Submit error');
          });
        },
      });
  }
}
